import React, { useEffect, useState, useContext } from "react";
import QuoteAcceptedModal from "components/QuoteAcceptedModal";
import { formatDateOnly } from "helpers/dateOnly";
import { discountBase, earlyPayFigures } from "helpers/earlyPay";
import EarlyPayBanner from "components/EarlyPayBanner";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import { ShareIcon, PrintIcon, DownloadIcon, TimesIcon, CheckMarkIcon, QuestionMarkIcon, TrashIcon, PlaneIcon, WarningIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { useToast } from "components/toast";
import { emailQuote } from "api/Quotes";
import { getQuotePDF, acceptOrDeny, getQuote } from "api/Quotes";
import { setQuoteSurcharge, FUEL_SURCHARGE_TYPE } from "api/FuelSurcharge";
import { declineQuote } from "api/QuoteExpiration";
import { getInvoicePDF, createInvoice, emailInvoice } from "api/Invoices";
import { getMapSnapshots } from "api/MulchMap";
import SnapshotIncludePicker from "components/SnapshotIncludePicker";
import { trackActivity } from "api/ActivityTracker";
import { useNavigate } from "react-router-dom";
import UserContext from "context/User";
import DeclineReasonModal from "components/DeclineReasonModal";
import TermsEditor from "components/TermsEditor";
import { updateQuote } from "api/Quotes";
import QuotePaymentTerms from "./QuotePaymentTerms";
import { trackQuoteAcceptance, identifyUser, trackGoal } from "helpers/zohoMA";

const Overview = ({ isCompany, company, quote, reloadQuote, onLockPrice, onRequireDeposit, onRegenerate }: { isCompany: boolean; company: any; quote: any; reloadQuote: (quote: any) => void; onLockPrice?: () => void; onRequireDeposit?: () => void; onRegenerate?: () => void }) => {
  const { user } = useContext<any>(UserContext);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [confirmAcceptModalOpen, setConfirmAcceptModalOpen] = useState<boolean>(false);
  const [confirmDenyModalOpen, setConfirmDenyModalOpen] = useState<boolean>(false);
  const [items, setItems] = useState<any[]>([]);
  const [sendSubmitting, setSendSubmitting] = useState<boolean>(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState<boolean>(false);
  const [quoteAccepted, setQuoteAccepted] = useState<boolean>(false);
  // Project the acceptance created/joined: the confirmation offers a PO/site-map upload to it.
  const [acceptedProjectId, setAcceptedProjectId] = useState<string | null>(null);
  const [acceptedProjectSeq, setAcceptedProjectSeq] = useState<number | null>(null);
  const [sendingInvoiceToBilling, setSendingInvoiceToBilling] = useState<boolean>(false);

  // Session 28 hotfix 6 (Phase 2): the picker component fully replaces
  // the Session 25 Batch #4 checkbox. Snapshot inclusion is now
  // persisted on `quote.mulchMapSnapshotIds`; the backend PDF gen path
  // reads the persisted list. Frontend just tells the picker how many
  // snapshots exist (so it decides to render the chip) and what the
  // initial selection is.
  const [snapshotCount, setSnapshotCount] = useState<number>(0);

  const columnHelper = createColumnHelper();
  const toast = useToast();
  const navigate = useNavigate();

  const cancelAcceptConfirm = () => {
    setConfirmAcceptModalOpen(false);
    setQuoteAccepted(false);
  };

  const cancelDenyConfirm = () => {
    setConfirmDenyModalOpen(false);
  };

  const toggleAcceptModal = () => {
    setConfirmAcceptModalOpen(!confirmAcceptModalOpen);
  };

  const toggleDenyModal = () => {
    setConfirmDenyModalOpen(!confirmDenyModalOpen);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "itemName",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Item</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.description, {
      id: "description",
      cell: (info: any) => <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Description</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>
            {info.getValue()} {row.unit}
          </Box>
        );
      },
      header: () => <span>Qty</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.price, {
      id: "rate",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => <span>Rate</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.price * row?.quantity, {
      id: "total",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500", textAlign: "right" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => (
        <Flex sx={{ width: "100%", alignItems: "center", justifyContent: "flex-end" }}>
          <Box>Amount</Box>
        </Flex>
      ),
      enableSorting: false,
    }),
  ];

  useEffect(() => {
    if (quote) {
      const flatItems = quote.items?.flat() || [];
      setItems(flatItems);
    }
  }, [quote]);

  // Session 25 Batch #4 / Session 28 hotfix 6: fetch snapshot count for
  // the attached map so the picker chip only appears when there’s
  // something to include. Clears when the map is detached.
  // Session 28 hotfix 11: gated on `isCompany` — the `getMapSnapshots`
  // endpoint is company-only, so clients/providers would 403 (and get
  // bounced by the global 403 interceptor) if we called it. The chip
  // itself is already company-only in the JSX.
  useEffect(() => {
    if (!isCompany) {
      setSnapshotCount(0);
      return;
    }
    const mulchMapId = quote && quote.mulchMapId;
    if (!mulchMapId) {
      setSnapshotCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const snapshots = await getMapSnapshots(mulchMapId);
      if (cancelled) return;
      const count = Array.isArray(snapshots) ? snapshots.length : 0;
      setSnapshotCount(count);
    })();
    return () => { cancelled = true; };
  }, [isCompany, quote && quote.mulchMapId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.add("Link copied to clipboard.", "var(--theme-ui-colors-success)");
  };

  const handleEmailQuote = async () => {
    setSendSubmitting(true);
    // Session 28 hotfix 6: no more includeMapSnapshot boolean. The
    // backend reads quote.mulchMapSnapshotIds directly.
    const res = await emailQuote({ data: { id: quote.id } });
    if (res.success) {
      toast.add("Quote emailed successfully.", "var(--theme-ui-colors-success)");
      
      // Build recipient emails array from quote data
      const recipientEmails: string[] = [];
      
      // Primary client contact email
      if (quote.primaryClient?.contactEmail) {
        recipientEmails.push(quote.primaryClient.contactEmail);
      }
      
      // CC recipients
      if (quote.contactsCC && Array.isArray(quote.contactsCC)) {
        recipientEmails.push(...quote.contactsCC);
      }
      
      // BCC recipients
      if (quote.contactsBCC && Array.isArray(quote.contactsBCC)) {
        recipientEmails.push(...quote.contactsBCC);
      }
      
      // Track email send
      void trackActivity({
        module: "quotes",
        action: "send_quote_email",
        actionType: "click",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: quote.id,
        metadata: {
          recipientEmails,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } else {
      toast.add("Failed to email quote.", "var(--theme-ui-colors-danger)");
    }
    setSendSubmitting(false);
  };

  const handleDownloadQuote = async () => {
    setDownloadSubmitting(true);
    // Session 28 hotfix 6: no more includeMapSnapshot boolean. The
    // backend reads quote.mulchMapSnapshotIds directly.
    const res = await getQuotePDF({ data: { id: quote.id } });
    
    // Track PDF generation
    void trackActivity({
      module: "quotes",
      action: "generate_quote_pdf",
      actionType: "click",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "quote",
      entityId: quote.id,
      metadata: {},
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    
    const link = document.createElement("a");
    link.href = res.result;
    link.download = `GMQ_${String(quote.sequence).padStart(5, "0")}${(quote.majorVersion > 1 || quote.minorVersion > 0) ? (quote.minorVersion > 0 ? "-" + quote.majorVersion + "." + quote.minorVersion : "-" + quote.majorVersion) : ""}_quote.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadSubmitting(false);
  };

  const handleSendInvoiceToBilling = async () => {
    setSendingInvoiceToBilling(true);
    
    try {
      const invoiceId = quote.invoice?.id;
      
      if (!invoiceId) {
        throw new Error('Invoice not found. Please refresh the page.');
      }
      
      // Send the invoice email
      const emailRes = await emailInvoice({ data: { id: invoiceId } });
      
      if (!emailRes.success) {
        throw new Error('Failed to send invoice email');
      }
      
      toast.add("Invoice sent to billing department with discount options.", "var(--theme-ui-colors-success)");
      
      // Track activity
      void trackActivity({
        module: "invoices",
        action: "send_invoice_with_discount_options",
        actionType: "click",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: quote.id,
        metadata: {
          sentToBilling: true,
          invoiceId: invoiceId,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      // Refetch the quote to get the updated invoice reference
      const updatedQuote = await getQuote({ data: { id: quote.id } });
      if (updatedQuote.id) {
        reloadQuote(updatedQuote);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.add("Failed to send invoice to billing.", "var(--theme-ui-colors-danger)");
    }
    
    setSendingInvoiceToBilling(false);
  };

  const handleViewOrPayInvoice = async () => {
    try {
      const invoiceId = quote.invoice?.id;
      
      if (!invoiceId) {
        throw new Error('Invoice not found. Please refresh the page.');
      }
      
      // Track view activity
      void trackActivity({
        module: "invoices",
        action: "view_or_pay_invoice",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "invoice",
        entityId: invoiceId,
        metadata: {
          source: "quote_acceptance_discount_popup",
          projectId: quote.project?.id || null,
          quoteId: quote.id,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      // Same tab (2026-10-08): clients land on the pay chooser (?pay=1);
      // staff just view it.
      navigate(`/invoices/${invoiceId}${isCompany ? "" : "?pay=1"}`);
    } catch (error) {
      console.error('Error opening invoice:', error);
      toast.add("Failed to open invoice.", "var(--theme-ui-colors-danger)");
    }
  };

  const submitAcceptOrDeny = async (accept: boolean) => {
    setSubmitting(true);
    const fromProject = quote.project ? true : false;
    const res = await acceptOrDeny({ data: { id: quote.id }, update: { status: accept ? 1 : 2, discountFlag: false } });

    if (res.quote && accept) {
      reloadQuote(res.quote);
      
      setAcceptedProjectId(res.project?.id ?? res.quote?.project?.id ?? quote.project?.id ?? null);
      setAcceptedProjectSeq(res.project?.sequence ?? res.quote?.project?.sequence ?? quote.project?.sequence ?? null);
      // Set state to show the discount information
      setQuoteAccepted(true);
      
      if (fromProject) {
        toast.add("Quote accepted.", "var(--theme-ui-colors-success");
      } else {
        toast.add("Quote accepted and new project created.", "var(--theme-ui-colors-success)");
      }
      
      if (Array.isArray(res.purchaseOrders)) {
        for (const po of res.purchaseOrders) {
          void trackActivity({
            module: "purchase_orders",
            action: "create_purchase_order",
            actionType: "create",
            userId: user?.id || null,
            companyId: user?.company?.id || null,
            entityType: "purchase_order",
            entityId: po.id,
            metadata: {
              projectId: res.project?.id || po.project?.id || null,
              quoteId: quote.id,
              providerId: po.provider?.id || null,
              providerEmail: po.provider?.contactEmail || null,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }
      }

      // Track quote acceptance
      void trackActivity({
        module: "quotes",
        action: "accept_quote",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: quote.id,
        metadata: {
          withDiscount: false,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      
      // Track project creation if a new project was created
      if (!fromProject && res.quote.project) {
        void trackActivity({
          module: "projects",
          action: "create_project",
          actionType: "create",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "project",
          entityId: res.quote.project.id,
          metadata: {
            clientId: res.quote.project.client?.id || quote.client?.id || null,
            projectName: res.quote.project.projectName || quote.projectName || null,
            quoteSequence: quote.sequence,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }

    if (res.quote && !accept) {
      reloadQuote(res.quote);
      toast.add("Quote denied.", "var(--theme-ui-colors-danger)");
      
      // Track quote denial
      void trackActivity({
        module: "quotes",
        action: "deny_quote",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: quote.id,
        metadata: {},
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      setConfirmDenyModalOpen(false);
    }

    setSubmitting(false);
  };

  // Calculate discount amounts
  const quoteTotal = quote?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
  // Savings on the goods only; the fuel surcharge is never discounted.
  const epf = earlyPayFigures(quoteTotal, discountBase(quote?.items));
  const fullDepositDiscount = epf.fullSave;
  const fullDepositAmount = epf.fullTotal;
  const halfDepositDiscount = epf.depositSave;
  const halfDepositAmount = epf.depositTotal;

  // Discount eligibility: must meet minimum AND not be a rush order
  const DISCOUNT_MINIMUM = 2000;
  const RUSH_THRESHOLD_DAYS = 5;
  const meetsMinimum = quoteTotal >= DISCOUNT_MINIMUM;
  const isRushOrder = (() => {
    // If project exists (post-acceptance), use the project's rush flag
    if (quote.project?.isRushOrder !== undefined) return quote.project.isRushOrder === true;
    // Pre-acceptance: compute from quote serviceDate vs today
    if (quote.serviceDate) {
      const serviceDate = moment(quote.serviceDate);
      const today = moment().startOf("day");
      const daysUntilService = serviceDate.diff(today, "days");
      return daysUntilService <= RUSH_THRESHOLD_DAYS;
    }
    return false;
  })();
  // N2 - a client on the deposit requirement is not offered the
  // early-payment incentive (mutually exclusive with the deposit). Reads
  // the billed client (primaryClient is the sub-client of a company
  // client). Mirrors the backend suppression in quote.service generatePdf.
  const clientRequiresDeposit =
    !!(quote as any).requiresDeposit ||
    (quote as any).client?.requiresDeposit === true ||
    (quote as any).primaryClient?.requiresDeposit === true;
  // Per-quote switch (2026-10-01): a deposit on this quote, or the savings
  // turned off on it, hides the early-payment lines like the client-wide N2.
  const quoteDepositPercent = (() => {
    const raw = Number(
      ((quote as any).requiresDeposit ? (quote as any).depositPercentage : null) ??
        (quote as any).client?.depositPercentage ??
        (quote as any).primaryClient?.depositPercentage ??
        50,
    );
    return raw > 0 && raw < 100 ? raw : 50;
  })();
  const isDiscountEligible =
    meetsMinimum && !isRushOrder && !clientRequiresDeposit && !(quote as any).earlyPaymentDiscountOff;
  
  // Calculate discount message based on acceptance status
  const getDiscountMessage = () => {
    if (quote.acceptedDate) {
      const acceptedDate = moment(quote.acceptedDate);
      const expiryDate = acceptedDate.clone().add(5, "days");
      const now = moment();
      
      // Only show specific date if we're within the 5-day window
      if (now.isBefore(expiryDate)) {
        return `by ${expiryDate.format("MMMM D, YYYY")}`;
      }
    }
    return "within 5 days of quote acceptance";
  };
  
  const discountMessage = getDiscountMessage();


  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Overview
      </H5>
      {/* Early-payment reminder on the accepted quote (2026-10-08). */}
      {!isCompany && quote?.id && Number(quote?.status) === 1 && <EarlyPayBanner quoteId={quote.id} />}
      <Flex sx={{ maxWidth: "1000px", width: "100%", margin: "0 auto 20px auto", alignItems: "flex-end", flexWrap: "wrap", gap: "10px" }}>
        <Flex sx={{ gap: "20px", flex: 1, alignItems: "center" }}>
          <Box sx={{ cursor: "pointer" }} onClick={handleCopyLink}>
            <ShareIcon fill={themevals.colors.dkgrey} width="20px" />
          </Box>
          {isCompany && (
            <Box sx={{ cursor: "pointer" }} onClick={handleEmailQuote}>
              {sendSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
              {!sendSubmitting && <PlaneIcon fill={themevals.colors.dkgrey} width="20px" />}
            </Box>
          )}
          <Box sx={{ cursor: "pointer" }} onClick={handleDownloadQuote}>
            {downloadSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
            {!downloadSubmitting && <DownloadIcon fill={themevals.colors.dkgrey} width="20px" />}
          </Box>
          {snapshotCount > 0 && isCompany && quote?.id && quote?.mulchMapId && (
            <SnapshotIncludePicker
              recordType="quote"
              recordId={quote.id}
              mulchMapId={quote.mulchMapId}
              initialSelectedIds={
                Array.isArray(quote.mulchMapSnapshotIds)
                  ? quote.mulchMapSnapshotIds
                  : []
              }
            />
          )}
        </Flex>
        <Flex sx={{ justifyContent: "flex-end", flex: 1, gap: "20px" }}>
          {((quote.status === 0 && quote.expiresAt && new Date(quote.expiresAt) < new Date()) || quote.status === 4) ? (
            <>
              <BasicButton scheme="dangerSecondary" type="button" onClick={toggleDenyModal}>
                <TimesIcon fill={themevals.colors.danger} /> Decline
              </BasicButton>
              {onRegenerate && (
                <BasicButton type="button" onClick={onRegenerate}
                  styles={{ backgroundColor: "#E65100", borderColor: "#E65100" }}
                >
                  Regenerate Quote
                </BasicButton>
              )}
            </>
          ) : (quote.status === 0 || (quote.status === 6 && quote.priceLockDepositStatus !== "received")) ? (
            <>
              <BasicButton scheme="dangerSecondary" type="button" onClick={toggleDenyModal}>
                <TimesIcon fill={themevals.colors.danger} /> {quote.status === 6 && quote.priceLockDepositStatus === "received" ? "Decline" : "Deny"}
              </BasicButton>
              {quote.status === 0 && onLockPrice && (
                <BasicButton type="button" onClick={onLockPrice}
                  styles={{ backgroundColor: themevals.colors.info, borderColor: themevals.colors.info }}
                >
                  Lock Price
                </BasicButton>
              )}
              <BasicButton type="button" $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}
                onClick={
                  // N2: a deposit-required client accepting an active quote
                  // is routed into the deposit gate (lock price + pay now)
                  // instead of accepting straight to a project.
                  quote.status === 0 && clientRequiresDeposit && onRequireDeposit
                    ? onRequireDeposit
                    : toggleAcceptModal
                }
                styles={quote.status === 6 && quote.priceLockDepositStatus === "received" ? { backgroundColor: "#48BB78", borderColor: "#48BB78" } : undefined}
              >
                <CheckMarkIcon fill={"#fff"} />
                {quote.status === 6 && quote.priceLockDepositStatus === "received"
                  ? "Confirm & Start Project"
                  : quote.status === 0 && clientRequiresDeposit
                    ? "Pay Deposit & Accept"
                    : "Accept"}
              </BasicButton>
            </>
          ) : (
            <Flex sx={{ alignItems: "center", justifyContent: "flex-end", gap: "20px" }}>
              <Flex
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  alignItems: quote.status === 1 ? "center" : "flex-end",
                  fontWeight: "600",
                  height: "36px",
                  color: quote.status === 1 ? themevals.colors.success : themevals.colors.danger,
                }}
              >
                {quote.status === 1 && (() => {
                  const who = [quote.acceptedBy?.firstName, quote.acceptedBy?.lastName].filter(Boolean).join(" ").trim();
                  const when = quote.acceptedDate ? moment(quote.acceptedDate).format("MMM D, YYYY") : null;
                  if (who && when) return `Accepted by ${who} on ${when}.`;
                  if (who) return `Accepted by ${who}.`;
                  if (when) return `Accepted on ${when}.`;
                  return "Accepted.";
                })()}
                {quote.status === 2 && (() => {
                  const who = [quote.deniedBy?.firstName, quote.deniedBy?.lastName].filter(Boolean).join(" ").trim();
                  const when = quote.deniedDate ? moment(quote.deniedDate).format("MMM D, YYYY") : null;
                  if (who && when) return `Denied by ${who} on ${when}.`;
                  if (who) return `Denied by ${who}.`;
                  if (when) return `Denied on ${when}.`;
                  return "Denied.";
                })()}
              </Flex>
              {quote.status === 1 && quote.project && (
                <Flex
                  onClick={() => navigate('/projects/' + quote.project.id)}
                  sx={{
                    fontFamily: "Poppins",
                    background: themevals.colors.success,
                    alignItems: "center",
                    padding: "0 15px",
                    borderRadius: "50px",
                    color: "white",
                    fontSize: "12px",
                    height: "36px",
                    cursor: "pointer",
                  }}
                >{`Project# GMP-${String(quote.project.sequence).padStart(5, "0")}`}</Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          margin: "0 auto 60px auto",
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          border: `1px solid ${themevals.colors.inputOutline}`,
          borderRadius: "6px",
          padding: "40px",
          backgroundColor: "#fff",
        }}
      >
        {/* 1. Header band — logo+company left, TITLE/#/ESTIMATED TOTAL right */}
        <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "20px", paddingBottom: "20px", borderBottom: "1px solid #000" }}>
          <Flex sx={{ flexDirection: "column", flex: 1 }}>
            {company?.data?.logo && (
              <img src={company.data.logo} alt="Company Logo" style={{ width: "auto", height: "40px", marginBottom: "12px", alignSelf: "flex-start" }} />
            )}
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px">
              {company.name}
            </P>
            {company.data?.address?.line1 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line1}
              </P>
            )}
            {company.data?.address?.line2 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line2}
              </P>
            )}
            {(company.data?.address?.city || company.data?.address?.state || company.data?.address?.zip) && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {`${company.data?.address?.city ?? ""}${company.data?.address?.city ? ", " : ""}${company.data?.address?.state ?? ""} ${company.data?.address?.zip ?? ""}`.trim()}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
            <Box sx={{ fontFamily: "Rubik-Medium, Rubik, sans-serif", fontSize: "22px", letterSpacing: "1px", color: "#000", textTransform: "uppercase" }}>
              Quote
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="11px" fontWeight="600" color={themevals.colors.dkgrey}>
              {`#GMQ-${String(quote.sequence).padStart(5, "0")}${(quote.majorVersion > 1 || quote.minorVersion > 0) ? (quote.minorVersion > 0 ? "-" + quote.majorVersion + "." + quote.minorVersion : "-" + quote.majorVersion) : ""}`}
            </P>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginTop: "14px" }}>
              Estimated Total
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="20px" fontWeight="500" color="#000">
              {quoteTotal?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </P>
          </Flex>
        </Flex>

        {/* 2. Meta section — QUOTE FOR (billing client) + meta rows */}
        <Grid gap={0} columns={[1, "1fr 1fr"]} sx={{ width: "100%", marginTop: "24px", paddingBottom: "16px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
          <Flex sx={{ flexDirection: "column" }}>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" }}>
              Quote For
            </Box>
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px" color="#000">
              {quote.primaryClient?.name ? quote.primaryClient.name : (quote.client?.name || "-")}
            </P>
            {quote.client?.name && quote.primaryClient?.name && quote.client.name !== quote.primaryClient.name && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {quote.client.name}
              </P>
            )}
            {(quote.primaryClient?.billingEmail || quote.primaryClient?.contactEmail) && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {quote.primaryClient?.billingEmail || quote.primaryClient?.contactEmail}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                Quote Date
              </Box>
              <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                {moment(quote.createdAt).format("MMM D, YYYY")}
              </P>
            </Flex>
            {quote.serviceDate && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Estimated Service Date
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {formatDateOnly(quote.serviceDate)}
                </P>
              </Flex>
            )}
            {quote.quoteRequest?.sequence && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  RFQ #
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {`GMR-${String(quote.quoteRequest.sequence).padStart(5, "0")}`}
                </P>
              </Flex>
            )}
            {quote.expiresAt && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Valid Until
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color={moment(quote.expiresAt).diff(moment(), "days") <= 7 ? themevals.colors.warning : "#000"}>
                  {moment(quote.expiresAt).format("MMM D, YYYY")}
                </P>
              </Flex>
            )}
          </Flex>
        </Grid>

        {/* 3. Job Site — single-row callout */}
        {(quote?.projectName || quote?.location?.address) && (
          <Box sx={{ width: "100%", marginTop: "16px" }}>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" }}>
              Job Site
            </Box>
            <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="12px" fontWeight="500" color="#000">
              {[
                quote?.projectName ? quote.projectName : "Instant Estimate",
                [quote?.location?.address?.line1, `${quote?.location?.address?.city ?? ""}, ${quote?.location?.address?.state ?? ""} ${quote?.location?.address?.postalCode ?? ""}`.trim()].filter(Boolean).join(", "),
              ].filter(Boolean).join("  ·  ")}
            </P>
          </Box>
        )}

        {/* 4. Payment options table */}
        <Box sx={{ width: "100%", marginTop: "24px" }}>
          {items && <TableBasic data={items} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} disableSorting />}
          {/* Fuel surcharge switch: staff, open quotes only (status 0). The
              line itself renders above like any other line. */}
          {isCompany && Number(quote?.status) === 0 && (() => {
            const hasFuel = (items ?? []).some((i: any) => i?.type === FUEL_SURCHARGE_TYPE);
            return (
              <Flex sx={{ justifyContent: "flex-end", marginTop: "8px" }}>
                <BasicButton
                  outline
                  scheme="secondary"
                  type="button"
                  keepText
                  onClick={async () => {
                    const r: any = await setQuoteSurcharge(quote.id, !hasFuel);
                    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
                    if (!hasFuel && !r?.added) return toast.add("No fuel surcharge applies to this quote (no line has a provider cost).", "var(--theme-ui-colors-warning)");
                    const fresh = await getQuote({ data: { id: quote.id } });
                    if (fresh?.id) reloadQuote(fresh);
                    toast.add(hasFuel ? "Fuel surcharge removed from this quote." : "Fuel surcharge added to this quote.", "var(--theme-ui-colors-success)");
                  }}
                >
                  {hasFuel ? "Remove fuel surcharge" : "Add fuel surcharge"}
                </BasicButton>
              </Flex>
            );
          })()}
        </Box>

        {/* 5. Totals block */}
        <Flex sx={{ justifyContent: "flex-end", width: "100%", marginTop: "24px" }}>
          <Box sx={{ width: "280px" }}>
            <Flex sx={{ justifyContent: "space-between", paddingBottom: "8px" }}>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" color={themevals.colors.dkgrey}>Subtotal</P>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" fontWeight="500" color="#000">
                {quoteTotal?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
              </P>
            </Flex>
            <Box sx={{ borderTop: "1px solid #000", paddingTop: "8px" }}>
              <Flex sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">Estimated Total</P>
                <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">
                  {quoteTotal?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            </Box>
            {clientRequiresDeposit && (
              <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginTop: "10px" }}>
                <P fontFamily="Poppins" fontSize="11px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">
                  {quoteDepositPercent}% deposit required to schedule
                </P>
                <P fontFamily="Poppins" fontSize="11px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">
                  {(Math.round(Number(quoteTotal || 0) * quoteDepositPercent) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            )}
            {isDiscountEligible && (
              <Box sx={{ marginTop: "10px" }}>
                <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
                  <P fontFamily="Poppins" fontSize="10px" marginTop="0" marginBottom="0" color={themevals.colors.success}>
                    Pay 100% {discountMessage} for 5% off
                  </P>
                  <P fontFamily="Poppins" fontSize="10px" marginTop="0" marginBottom="0" color={themevals.colors.success}>
                    {fullDepositAmount?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                  </P>
                </Flex>
                <Flex sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
                  <P fontFamily="Poppins" fontSize="10px" marginTop="0" marginBottom="0" color={themevals.colors.success}>
                    Pay 50% {discountMessage} for 3% off
                  </P>
                  <P fontFamily="Poppins" fontSize="10px" marginTop="0" marginBottom="0" color={themevals.colors.success}>
                    {halfDepositAmount?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                  </P>
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>

        {isCompany && (
          <QuotePaymentTerms
            quote={quote}
            onSaved={async () => {
              const fresh = await getQuote({ data: { id: quote.id } });
              if (fresh?.id) reloadQuote(fresh);
            }}
          />
        )}

        {/* 6. Terms footnote */}
        <Box sx={{ width: "100%", marginTop: "40px", paddingTop: "20px", borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
          {isCompany ? (
            /* Staff: editable per-record T&C override (Session 59, Part B).
               Client view keeps the read-only rendered terms below. */
            <TermsEditor
              docType="quote"
              record={quote}
              update={updateQuote}
              onSaved={async () => {
                // reloadQuote only sets state to what it's given (no
                // refetch), so pull the fresh record first.
                const fresh = await getQuote({ data: { id: quote.id } });
                if (fresh?.id) reloadQuote(fresh);
              }}
            />
          ) : (
            <>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "6px" }}>
                Terms
              </Box>
              <P color={themevals.colors.dkgrey} marginBottom="0" marginTop="0" fontFamily="Poppins" fontSize="10px" lineHeight="16px" style={{ whiteSpace: "pre-wrap" }}>
                {quote?.resolvedTerms || ""}
              </P>
            </>
          )}
        </Box>
      </Box>
      
      {/* Accept Quote Modal */}
      {confirmAcceptModalOpen && !quoteAccepted && (
        <BasicModal
          title={quote.status === 6 && quote.priceLockDepositStatus === "received" ? "Confirm Project" : "Accept Quote?"}
          cancel_click={() => cancelAcceptConfirm()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => submitAcceptOrDeny(true)}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={quote.status === 6 && quote.priceLockDepositStatus === "received" ? { margin: "0 0.625rem .25rem 0", backgroundColor: "#48BB78", borderColor: "#48BB78" } : { margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <CheckMarkIcon fill={"#fff"} />}
              {quote.status === 6 && quote.priceLockDepositStatus === "received" ? "Confirm & Start Project" : "Accept quote"}
            </BasicButton>
          }
        >
          {quote.status === 6 && quote.priceLockDepositStatus === "received" ? (
            <div style={{ fontFamily: "Poppins, sans-serif" }}>
              <P lineHeight="22px" marginBottom="12px">
                {"You're confirming this quote at the locked price. Here's what happens next:"}
              </P>
              <div style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px" }}>
                  <span style={{ color: themevals.colors.dkgrey }}>Quote Total</span>
                  <span style={{ fontWeight: 600 }}>{"$" + Number(quote.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px", color: "#276749" }}>
                  <span>Deposit Paid</span>
                  <span style={{ fontWeight: 600 }}>{"- $" + Number(quote.priceLockDepositAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", borderTop: "1px solid #E2E8F0", paddingTop: "6px", marginTop: "2px" }}>
                  <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                  <span style={{ fontWeight: 700 }}>{"$" + Number((quote.total || 0) - (quote.priceLockDepositAmount || 0)).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <P lineHeight="20px" marginBottom="0px" fontSize="12px" color={themevals.colors.dkgrey}>
                A project and invoice will be created. Your deposit will be automatically credited to your invoice.
              </P>
            </div>
          ) : (
            <P lineHeight="22px" marginBottom="0px">
              Are you sure you want to accept this quote?
            </P>
          )}
        </BasicModal>
      )}
      
      {/* Compact post-accept window, shared with the dashboard (components/QuoteAcceptedModal). */}
      {confirmAcceptModalOpen && quoteAccepted && (
        <QuoteAcceptedModal
          quote={quote}
          isStaff={isCompany}
          projectId={acceptedProjectId}
          projectSequence={acceptedProjectSeq}
          onClose={() => cancelAcceptConfirm()}
          onPay={handleViewOrPayInvoice}
          onSendToBilling={handleSendInvoiceToBilling}
          sendingToBilling={sendingInvoiceToBilling}
        />
      )}
      
      {/* Deny Quote Modal */}
      {confirmDenyModalOpen && (
        <DeclineReasonModal
          type="quote"
          onConfirm={async (data: any) => {
            setSubmitting(true);
            const res = await declineQuote(quote.id, data);
            setSubmitting(false);
            if (res?.success) {
              toast.add("Quote denied.", "var(--theme-ui-colors-success)");
              setConfirmDenyModalOpen(false);
              const updated = await getQuote({ data: { id: quote.id } });
              if (updated?.id) reloadQuote(updated);
            } else {
              toast.add(res?.message || "Failed to deny quote.", "var(--theme-ui-colors-danger)");
            }
          }}
          onCancel={() => setConfirmDenyModalOpen(false)}
          submitting={submitting}
        />
      )}
    </>
  );
};

export default Overview;
